import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import Spinner from '@/components/ui/Spinner';
import StatusPill from '@/components/ui/StatusPill';
import type { ActiveProduction } from '@/hooks/useDashboard';
import { crewPhaseLabel, gateLabel, productionTypeMeta } from '@/lib/labels';
import { useLang } from '@/lib/lang';
import { relativeTime } from '@/lib/time';
import { cn } from '@/lib/utils';

/**
 * A production that is currently running — or, more urgently, one that has PAUSED
 * and is waiting on the producer. A waiting gate is styled as the loudest thing on
 * the page because nothing else moves until it's resolved.
 */
export default function InFlightCard({
  production,
  needsReview,
  type,
}: {
  production: ActiveProduction;
  /** Open review-gate phase, if the run is paused waiting for approval. */
  needsReview?: string;
  type?: string;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation(['dashboard', 'common']);
  const lang = useLang();
  const meta = productionTypeMeta(t, type);

  return (
    <button
      type="button"
      onClick={() => navigate(`/projects/${production.project_id}/productions/${production.production_id}`)}
      className={cn(
        'flex w-full items-center justify-between gap-4 rounded-xl border px-5 py-4 text-left transition-all',
        'hover:-translate-y-px',
        needsReview
          ? 'border-primary/60 bg-primary/10 hover:border-primary'
          : 'border-success/30 bg-success/[0.06] hover:border-success/60',
      )}
    >
      <div className="flex min-w-0 items-center gap-3">
        {!needsReview && <Spinner />}
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="truncate font-display text-[15px] font-semibold">
              {production.project_title || production.project_id}
            </h4>
            <StatusPill tone="neutral">{meta.label}</StatusPill>
          </div>
          <div className="mt-1 text-[11.5px] text-faint">
            {needsReview ? (
              <span className="font-medium text-primary">{t('flight.needsApproval', { what: gateLabel(t, needsReview) })}</span>
            ) : (
              <>
                {crewPhaseLabel(t, production.phase) || production.status}
                {production.started_at && ` · ${t('flight.started', { when: relativeTime(production.started_at, lang) })}`}
              </>
            )}
          </div>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-3">
        {needsReview ? (
          <StatusPill tone="attention">{t('flight.reviewNow')}</StatusPill>
        ) : (
          <StatusPill status={production.status}>{production.status}</StatusPill>
        )}
        <ArrowRight className="h-4 w-4 text-faint" />
      </div>
    </button>
  );
}
